import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import DatePicker, { registerLocale } from 'react-datepicker'
import { th } from 'date-fns/locale'
registerLocale('th', th)

import {
  getLessons,
  fetchExamBank,
  createTest,
  getTestDetail,
  updateTest
} from '@/api/kuizy'
import {
  typeExam_Order,
  typeOption_Order,
  levelMapping,
  examTypeMapping,
  typeSummaryMapping
} from '@/settings/master'

import {
  Layers,
  ListOrdered,
  LoaderCircle,
  Save,
  ClipboardList,
  ChevronUp,
  ChevronDown,
  Calendar,
  Plus,
  Clock,
  Percent,
  Shuffle,
  List,
  Check,
  Square,
  CheckSquare,
  ClipboardPlus,
  ClipboardEdit,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ClipboardPenLine,
  ChevronLeft
} from 'lucide-react'
import Select from 'react-select'
import { motion, AnimatePresence } from 'framer-motion'
import ConfirmModal from '@/widgets/ConfirmModal'
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const iso = d => (d ? new Date(d) : null)
const slide = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } }
const rs = { classNamePrefix: 'rs', isSearchable: true }

export default function TestBuilderPage() {
  const { cid: cidRaw, tid } = useParams()
  const editing = !!tid
  const uid = localStorage.userId || '1'
  const navigate = useNavigate()
  const contentId = decodeURIComponent(cidRaw || '')
  const [testQuestionIds, setTestQuestionIds] = useState([])

  const [metaOpen, setMetaOpen] = useState(true)
  const [meta, setMeta] = useState({
    name: '',
    desc: '',
    start: null,
    end: null,
    dur: '60',
    pass: '80',
    exOrder: 'normal',
    opOrder: 'normal'
  })
  const [saving, setSaving] = useState(false)

  const [lessons, setLessons] = useState([])
  const [loadingLs, setLL] = useState(true)
  const [cfg, setCfg] = useState({})
  const [selIds, setSel] = useState({})
  const [qPool, setPool] = useState({})
  const [loadingQ, setLQ] = useState(false)
  const [search, setSearch] = useState('')
  const totalSel = useMemo(() => Object.values(selIds).flat().length, [selIds])

  const [fLevel, setFLevel] = useState('')
  const [fExam, setFExam] = useState('')
  const [fSum, setFSum] = useState('')
  const [sortKey, setSortKey] = useState('')
  const [sortDir, setSortDir] = useState('asc')

  const [recentlyMovedId, setRecentlyMovedId] = useState(null)
  const [lastMovedId, setLastMovedId] = useState(null)


  const [exp, setExp] = useState(new Set())
  const toggleExp = id => {
    const s = new Set(exp)
    s.has(id) ? s.delete(id) : s.add(id)
    setExp(s)
  }
  const [alert, setAlert] = useState({ open: false, msg: '' })
  const toast = m => setAlert({ open: true, msg: m })

  const reorderMode = meta.exOrder === 'normal'
  useEffect(() => {
    if (!editing) return
      ; (async () => {
        try {
          const { data } = await getTestDetail(tid, localStorage.orgId)
          if (!data) return toast('ไม่พบชุดข้อสอบนี้')

          // -------------------------
          // 1. meta (เดิม)
          // -------------------------
          setMeta({
            name: data.name,
            desc: data.description || '',
            start: iso(data.start_time),
            end: iso(data.end_time),
            dur: String(data.duration ?? 60),
            pass: String(data.criteria ?? 80),
            exOrder: data.exam_order || 'normal',
            opOrder: data.option_order || 'normal'
          })

          // -------------------------
          // 2. selIds / cfg (เดิม)
          // -------------------------
          const g = {}
            ; (data.questions || []).forEach(q => {
              const lid = q.lesson_id
              if (!lid) return
              g[lid] ??= []
              g[lid].push(q.id)
            })

          setCfg(
            Object.fromEntries(
              Object.entries(g).map(([k, v]) => [
                k,
                { qty: v.length, random: false }
              ])
            )
          )
          setSel(g)

          // -------------------------
          // ⭐ 3. ตั้งลำดับข้อสอบจาก backend
          // -------------------------
          setTestQuestionIds(
            (data.questions || []).map(q => q.id)
          )

          // -------------------------
          // 4. load question bank (เดิม)
          // -------------------------
          Object.keys(g).forEach(loadQs)

        } catch {
          toast('โหลดข้อมูลผิดพลาด')
        }
      })()
  }, [editing, tid])


  useEffect(() => {
    if (!contentId) return
    setLL(true)
    getLessons({ contentId, pageSize: 'all' })
      .then(({ data }) => setLessons(data.data || []))
      .finally(() => setLL(false))
  }, [contentId])

  const loadQs = async lid => {
    if (qPool[lid]) return
    setLQ(true)
    const { data } = await fetchExamBank({
      contentId,
      lessonId: lid,
      pageSize: 'all'
    })
    setPool(p => ({ ...p, [lid]: data.data || [] }))
    setLQ(false)
  }

  const toggleQ = q => {
    const lid = q.lesson_id
    if (!cfg[lid]) return
    const limit = cfg[lid].qty
    setSel(prev => {
      const list = prev[lid] || []
      if (list.includes(q.id)) return { ...prev, [lid]: list.filter(id => id !== q.id) }
      if (list.length >= limit) {
        toast(`เลือกได้ไม่เกิน ${limit} ข้อ`)
        return prev
      }
      return { ...prev, [lid]: [...list, q.id] }
    })
  }


  const applyCfg = useCallback(
    (lid, c) => {
      if (!qPool[lid]) return
      let arr = [...qPool[lid]]
      if (c.random) arr.sort(() => 0.5 - Math.random())
      arr = arr.slice(0, Math.min(c.qty, arr.length))
      setSel(p => ({ ...p, [lid]: arr.map(q => q.id) }))
    },
    [qPool]
  )

  const updCfg = (lid, f, v) =>
    setCfg(p => {
      const n = { ...p[lid], [f]: v }
      applyCfg(lid, n)
      return { ...p, [lid]: n }
    })

  const toggleLesson = ls =>
    setCfg(p => {
      const on = !!p[ls.lesson_id]
      const n = { ...p }
      if (on) {
        delete n[ls.lesson_id]
        const r = { ...selIds }
        delete r[ls.lesson_id]
        setSel(r)
      } else {
        n[ls.lesson_id] = { qty: ls.question_count, random: false }
        loadQs(ls.lesson_id).then(() => applyCfg(ls.lesson_id, n[ls.lesson_id]))
      }
      return n
    })

  const baseList = useMemo(
    () => Object.keys(cfg).flatMap(lid => qPool[lid] || []),
    [cfg, qPool]
  )

  const filtered = useMemo(() => {
    let arr = [...baseList]
    if (search.trim()) {
      const k = search.toLowerCase()
      arr = arr.filter(q => q.question.toLowerCase().includes(k))
    }
    if (fLevel) arr = arr.filter(q => q.level === fLevel)
    if (fExam) arr = arr.filter(q => q.exam_type === fExam)
    if (fSum) arr = arr.filter(q => q.type_summary === fSum)
    return arr
  }, [baseList, search, fLevel, fExam, fSum])

  const visible = useMemo(() => {
    if (!sortKey) return filtered
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? ''
      const bv = b[sortKey] ?? ''
      return av > bv ? dir : av < bv ? -dir : 0
    })
  }, [filtered, sortKey, sortDir])

  const [allOn, setAll] = useState(false)
  const toggleAll = on => {
    const nSel = { ...selIds }
    visible.forEach(q => {
      const lid = q.lesson_id
      if (!cfg[lid]) return
      const lim = cfg[lid].qty
      const s = new Set(nSel[lid] || [])
      on ? s.add(q.id) : s.delete(q.id)
      if (s.size <= lim) nSel[lid] = [...s]
    })
    setSel(nSel)
    setAll(on)
  }

  const save = async () => {
    if (!meta.name.trim() || totalSel === 0) return
    setSaving(true)
    const body = {
      user_id: uid,
      content_id: contentId,
      question_ids: testQuestionIds,
      name: meta.name.trim(),
      description: meta.desc.trim(),
      start_time: meta.start?.toISOString() || null,
      end_time: meta.end?.toISOString() || null,
      duration: +meta.dur || 60,
      criteria: +meta.pass || 80,
      exam_order: meta.exOrder,
      option_order: meta.opOrder
    }
    try {
      editing
        ? await updateTest({ ...body, test_id: tid })
        : await createTest(body)
      navigate('/tests')
    } catch {
      toast('บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const headerBtn = (k, label, cls = '') => {
    if (reorderMode) return <span>{label}</span>
    const active = sortKey === k
    const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown
    return (
      <button
        onClick={() => {
          if (sortKey !== k) {
            setSortKey(k)
            setSortDir('asc')
          } else setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
        }}
        className={`inline-flex items-center gap-1 ${cls}`}
      >
        {label} <Icon size={14} className={active ? 'text-primary' : 'text-gray-500'} />
      </button>
    )
  }

  const visibleOrdered = useMemo(() => {
    if (!reorderMode) return visible

    // map id -> index ตามลำดับข้อสอบ
    const orderMap = new Map(
      testQuestionIds.map((id, idx) => [id, idx])
    )

    return [...visible].sort((a, b) => {
      const ia = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity
      const ib = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity
      return ia - ib
    })
  }, [visible, testQuestionIds, reorderMode])

  useEffect(() => {
    const selectedNow = Object.values(selIds).flat()

    setTestQuestionIds(prev => {
      // ลบข้อที่ถูก uncheck
      let next = prev.filter(id => selectedNow.includes(id))

      // เพิ่มข้อใหม่ต่อท้าย
      selectedNow.forEach(id => {
        if (!next.includes(id)) next.push(id)
      })

      return next
    })
  }, [selIds])



  const orderedSelected = useMemo(() => {
    return Object.values(selIds).flat()
  }, [selIds])

  const orderedQuestions = useMemo(() => {
    const map = {}
    Object.values(qPool).flat().forEach(q => (map[q.id] = q))
    return orderedSelected.map(id => map[id]).filter(Boolean)
  }, [orderedSelected, qPool])

  const dragList = reorderMode ? orderedQuestions : visible

  const handleDragEnd = ({ active, over }) => {
    if (!reorderMode) return
    if (!over || active.id === over.id) return

    setTestQuestionIds(prev => {
      const oldIndex = prev.indexOf(active.id)
      const newIndex = prev.indexOf(over.id)
      if (oldIndex === -1 || newIndex === -1) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })

    // ⭐ เกิดขึ้น "หลังวาง" เท่านั้น
    setLastMovedId(active.id)
  }

  return (
    <motion.section
      {...slide}
      transition={{ duration: 0.35 }}
      className="flex flex-col gap-6 p-4 sm:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Link to={editing ? `/tests` : `/content/${encodeURIComponent(contentId)}/lessons`}
            className="text-primary font-bold text-base hover:underline mr-2">
            <ChevronLeft size={30} />
          </Link>
          {editing ? <ClipboardEdit size={20} /> : <ClipboardPlus size={20} />}{' '}
          {editing ? 'แก้ไขชุดข้อสอบ' : 'สร้างชุดข้อสอบ'}
        </h1>
        <div className="flex flex-wrap gap-2">
          <button
            disabled={saving || !meta.name.trim() || totalSel === 0}
            onClick={save}
            className="inline-flex items-center gap-1 bg-primary hover:bg-secondary disabled:opacity-50 text-white rounded-lg px-3 py-2 text-sm"
          >
            {saving ? (
              <LoaderCircle className="animate-spin" size={18} />
            ) : (
              <Save size={16} />
            )}
            {editing ? 'บันทึก' : 'สร้าง'} ({totalSel})
          </button>
          <button
            onClick={() => navigate('/tests')}
            className="inline-flex items-center gap-1 bg-info hover:bg-gray-200 rounded-lg px-3 py-2 text-sm"
          >
            <ClipboardList size={16} /> ชุดข้อสอบ
          </button>
          <button
            onClick={() => setMetaOpen(o => !o)}
            className="inline-flex items-center gap-1 bg-info hover:bg-gray-200 rounded-lg px-3 py-2 text-sm"
          >
            {metaOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}{' '}
            {metaOpen ? 'ย่อแผง' : 'ตั้งค่า'}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {metaOpen && (
          <motion.div
            key="meta"
            {...slide}
            transition={{ duration: 0.25 }}
            className="bg-white border border-gray-200 rounded-xl shadow-md p-4 sm:p-6"
          >
            <div className="grid gap-6 md:grid-cols-2">
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-1 text-sm">
                  <ClipboardList size={16} /> ชื่อชุดข้อสอบ <span className="text-red-500 font-bold">*</span>
                </label>
                <input
                  value={meta.name}
                  onChange={e => setMeta({ ...meta, name: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                />
                <label className="flex items-center gap-1 text-sm">
                  <ClipboardPenLine size={16} /> คำอธิบาย
                </label>
                <textarea
                  rows={4}
                  value={meta.desc}
                  onChange={e => setMeta({ ...meta, desc: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                />
                <label className="flex items-center gap-1 text-sm">
                  <Calendar size={16} /> วันที่เวลาเริ่มการใช้งาน (เว้นว่าง = ไม่จำกัด)
                </label>
                <DatePicker
                  locale="th"
                  selected={meta.start}
                  onChange={d => setMeta({ ...meta, start: d })}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="d MMM yyyy HH:mm"
                  placeholderText="เลือกวัน/เวลา"
                  className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm"
                />
                <label className="flex items-center gap-1 text-sm">
                  <Calendar size={16} /> วันที่เวลาสิ้นสุดการใช้งาน (เว้นว่าง = ไม่จำกัด)
                </label>
                <DatePicker
                  locale="th"
                  selected={meta.end}
                  onChange={d => setMeta({ ...meta, end: d })}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="d MMM yyyy HH:mm"
                  placeholderText="เลือกวัน/เวลา"
                  className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm"
                  minDate={meta.start || undefined}
                />
              </div>
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-1 text-sm">
                  <Clock size={16} /> ระยะเวลา (นาที)
                </label>
                <input
                  type="number"
                  min={0}
                  max={1000}
                  step={1}
                  value={meta.dur}
                  onKeyDown={e => {
                    if (e.key === '-' || e.key === 'e') {
                      e.preventDefault()
                    }
                  }}
                  onChange={e => {
                    let value = e.target.value

                    if (value === "") {
                      setMeta({ ...meta, dur: "" })
                      return
                    }

                    value = Number(value)

                    if (value < 0) value = 0
                    if (value > 1000) value = 1000

                    setMeta({ ...meta, dur: value })
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                />

                <label className="flex items-center gap-1 text-sm">
                  <Percent size={16} /> เกณฑ์ผ่าน (%)
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={meta.pass}
                  onKeyDown={e => {
                    if (e.key === '-' || e.key === 'e') {
                      e.preventDefault()
                    }
                  }}
                  onChange={e => {
                    let value = e.target.value

                    if (value === "") {
                      setMeta({ ...meta, pass: "" })
                      return
                    }

                    value = Number(value)

                    if (value < 0) value = 0
                    if (value > 100) value = 100

                    setMeta({ ...meta, pass: value })
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                />

                <label className="flex items-center gap-1 text-sm">
                  <ListOrdered size={16} /> เรียงข้อสอบ
                </label>
                <Select
                  {...rs}
                  options={Object.entries(typeExam_Order).map(([v, l]) => ({
                    value: v,
                    label: l
                  }))}
                  value={Object.entries(typeExam_Order)
                    .map(([v, l]) => ({ value: v, label: l }))
                    .find(o => o.value === meta.exOrder)}
                  onChange={o => setMeta({ ...meta, exOrder: o.value })}
                />
                <label className="flex items-center gap-1 text-sm">
                  <ListOrdered size={16} /> เรียงตัวเลือก
                </label>
                <Select
                  {...rs}
                  options={Object.entries(typeOption_Order).map(([v, l]) => ({
                    value: v,
                    label: l
                  }))}
                  value={Object.entries(typeOption_Order)
                    .map(([v, l]) => ({ value: v, label: l }))
                    .find(o => o.value === meta.opOrder)}
                  onChange={o => setMeta({ ...meta, opOrder: o.value })}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-4 md:grid-cols-[260px_1fr]">
        <div className="bg-white border border-gray-200 rounded-xl shadow-md p-4 flex flex-col gap-3 overflow-y-auto max-h-[70vh]">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <ListOrdered size={16} /> เลือกบทเรียน{' '}
            <span className="text-primary">({totalSel})</span>
          </h3>
          {loadingLs ? (
            <div className="flex justify-center py-10">
              <LoaderCircle className="animate-spin text-primary" />
            </div>
          ) : lessons.length === 0 ? (
            <div className="text-center py-8">
              <p className="mb-4">กรุณาเพิ่มบทเรียนก่อน</p>
              <button
                onClick={() => navigate(`/content/${contentId}/lessons/`)}
                className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm"
              >
                <Plus size={16} /> เพิ่มบทเรียน
              </button>
            </div>
          ) : (
            lessons.map(ls => {
              const active = !!cfg[ls.lesson_id]
              const selCt = (selIds[ls.lesson_id] || []).length
              const c = cfg[ls.lesson_id]
              return (
                <div key={ls.lesson_id} className="border-b border-gray-100 pb-3 mb-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleLesson(ls)}
                    />
                    <span>
                      {ls.lesson_name}{' '}
                      <small>
                        ({selCt}/{ls.question_count})
                      </small>
                    </span>
                  </label>
                  {active && (
                    <div className="flex items-center gap-3 mt-2 pl-6">
                      <input
                        type="number"
                        min={1}
                        max={ls.question_count}
                        value={c.qty}
                        onChange={e =>
                          updCfg(
                            ls.lesson_id,
                            'qty',
                            Math.max(
                              1,
                              Math.min(ls.question_count, +e.target.value || 1)
                            )
                          )
                        }
                        className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm text-center"
                      />
                      <div className="flex flex-col gap-1 text-sm">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`mode-${ls.lesson_id}`}
                            value="manual"
                            checked={!c.random}
                            onChange={() => updCfg(ls.lesson_id, 'random', false)}
                            className="appearance-none w-4 h-4 border-2 border-primary rounded-full checked:bg-primary checked:border-primary focus:ring-2 focus:ring-primary/50"
                          />
                          <List size={14} /> เลือกเอง
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`mode-${ls.lesson_id}`}
                            value="random"
                            checked={c.random}
                            onChange={() => updCfg(ls.lesson_id, 'random', true)}
                            className="appearance-none w-4 h-4 border-2 border-primary rounded-full checked:bg-primary checked:border-primary focus:ring-2 focus:ring-primary/50"
                          />
                          <Shuffle size={14} /> สุ่ม
                          {c.random && (
                            <button
                              onClick={() => applyCfg(ls.lesson_id, c)}
                              className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-2 py-1 text-xs"
                            >
                              สุ่มใหม่
                            </button>
                          )}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-md flex flex-col">
          <div className="flex flex-wrap gap-2 border-b border-gray-200 p-3">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="ค้นหาคำถาม..."
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[140px] focus:ring-2 focus:ring-primary"
            />
            <Select
              {...rs}
              className="min-w-[140px] flex-1 sm:flex-none"
              options={[
                { value: '', label: 'ระดับทั้งหมด' },
                ...Object.entries(levelMapping).map(([v, l]) => ({
                  value: v,
                  label: l
                }))
              ]}
              value={
                [
                  { value: '', label: 'ระดับทั้งหมด' },
                  ...Object.entries(levelMapping).map(([v, l]) => ({
                    value: v,
                    label: l
                  }))
                ].find(o => o.value === fLevel)
              }
              onChange={o => setFLevel(o.value)}
            />
            <Select
              {...rs}
              className="min-w-[140px] flex-1 sm:flex-none"
              options={[
                { value: '', label: 'ประเภทข้อสอบทั้งหมด' },
                ...Object.entries(examTypeMapping).map(([v, l]) => ({
                  value: v,
                  label: l
                }))
              ]}
              value={
                [
                  { value: '', label: 'ประเภทข้อสอบทั้งหมด' },
                  ...Object.entries(examTypeMapping).map(([v, l]) => ({
                    value: v,
                    label: l
                  }))
                ].find(o => o.value === fExam)
              }
              onChange={o => setFExam(o.value)}
            />
            <Select
              {...rs}
              className="min-w-[140px] flex-1 sm:flex-none"
              options={[
                { value: '', label: 'ประเภทคำตอบทั้งหมด' },
                ...Object.entries(typeSummaryMapping).map(([v, l]) => ({
                  value: v,
                  label: l
                }))
              ]}
              value={
                [
                  { value: '', label: 'ประเภทคำตอบทั้งหมด' },
                  ...Object.entries(typeSummaryMapping).map(([v, l]) => ({
                    value: v,
                    label: l
                  }))
                ].find(o => o.value === fSum)
              }
              onChange={o => setFSum(o.value)}
            />
            <span className="text-xs text-gray-600 ml-auto pt-2">
              รวม {visible.length} | เลือกแล้ว {totalSel}
            </span>
          </div>

          <div className="overflow-x-auto">
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={visibleOrdered.map(q => q.id)}
                strategy={verticalListSortingStrategy}
              >
                <table className="min-w-[880px] w-full text-sm">
                  <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
                    <tr>
                      <th className="w-12 text-center"></th>
                      <th className="py-2 px-3 w-12 text-left">
                        <button onClick={() => toggleAll(!allOn)} className="text-primary hover:text-primary/80">
                          {allOn ? <CheckSquare size={16} /> : <Square size={16} />}
                        </button>
                      </th>
                      <th className="w-12 text-center">#</th>
                      <th className="min-w-[70px] text-center hidden lg:table-cell">
                        {headerBtn('lesson_name', 'บทเรียน', 'justify-center')}
                      </th>
                      <th className="min-w-[200px]">
                        {headerBtn('question', 'ข้อสอบ')}
                      </th>
                      <th className="min-w-[100px] text-center hidden lg:table-cell">
                        {headerBtn('level', 'ระดับความยาก', 'justify-center')}
                      </th>
                      <th className="min-w-[100px] text-center hidden lg:table-cell">
                        {headerBtn('exam_type', 'ประเภทข้อสอบ', 'justify-center')}
                      </th>
                      <th className="min-w-[100px] text-center hidden lg:table-cell">
                        {headerBtn('type_summary', 'ประเภทคำตอบ', 'justify-center')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingQ ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8">
                          <LoaderCircle className="animate-spin text-primary" />
                        </td>
                      </tr>
                    ) : visible.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-gray-500">
                          ไม่พบคำถาม
                        </td>
                      </tr>
                    ) : (
                      visibleOrdered.map((q, i) => {
                        const checked = (selIds[q.lesson_id] || []).includes(q.id)
                        const canDrag = reorderMode && checked

                        const isEx = exp.has(q.id)
                        return (
                          <SortableRow
                            key={q.id} q={q} canDrag={canDrag} highlight={lastMovedId === q.id}
                            className={`${checked ? 'bg-primary/10' : 'odd:bg-white even:bg-gray-50'} hover:bg-primary/5 active:bg-primary/10`}
                          >
                            {!checked && (
                              <td className="w-12 text-center px-3 py-2">
                                <span className="cursor-grab text-gray-400">
                                  ☰
                                </span>
                              </td>
                            )}
                            <td
                              className="py-2 px-3 text-center"
                              onClick={() => toggleQ(q)}
                            >
                              {checked ? <CheckSquare size={16} /> : <Square size={16} />}
                            </td>

                            <td className="text-center px-3 py-2" >{i + 1}</td>
                            <td className="px-3 py-2 hidden lg:table-cell text-center" >
                              {q.lesson_name}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-start gap-2">
                                <button
                                  onClick={() => toggleExp(q.id)}
                                  className="text-primary shrink-0 mt-[2px]"
                                >
                                  {isEx ? (
                                    <ChevronUp size={14} />
                                  ) : (
                                    <ChevronDown size={14} />
                                  )}
                                </button>
                                <span className="whitespace-pre-line break-words" >
                                  {q.question}
                                </span>
                              </div>
                              {isEx && (
                                <ul className="mt-2 ml-6 list-disc text-xs space-y-1">
                                  {q.answer.map((a, idx) => {
                                    const k = Object.keys(a)[0]
                                    const v = a[k]
                                    const desc = typeof v === 'object' ? v.description : v
                                    return (
                                      <li
                                        key={k}
                                        className={q.correct.includes(k) ? 'text-green-700' : ''}
                                      >
                                        {String.fromCharCode(97 + idx)}. {desc}
                                      </li>
                                    )
                                  })}
                                </ul>
                              )}
                            </td>
                            <td className="text-center hidden lg:table-cell px-3 py-2">
                              {levelMapping[q.level]}
                            </td>
                            <td className="text-center hidden lg:table-cell px-3 py-2">
                              {examTypeMapping[q.exam_type]}
                            </td>
                            <td className="text-center hidden lg:table-cell px-3 py-2">
                              {typeSummaryMapping[q.type_summary]}
                            </td>
                          </SortableRow>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {alert.open && (
          <ConfirmModal
            type="alert"
            title="แจ้งเตือน"
            message={alert.msg}
            onClose={() => setAlert({ open: false, msg: '' })}
          />
        )}
      </AnimatePresence>
    </motion.section>
  )
}


function SortableRow({ q, canDrag, highlight, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: q.id,
    disabled: !canDrag,
  })

  const style = transform
    ? {
      transform: CSS.Transform.toString(transform),
      transition,
    }
    : undefined

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`
        align-top transition-colors
        ${isDragging ? 'bg-primary/20' : ''}
        ${highlight && !isDragging ? 'bg-pink-100' : ''}
      `}
    >
      {canDrag && (
        <td
          {...listeners}
          className="
            px-2 text-gray-400 text-center
            cursor-grab active:cursor-grabbing
            hover:text-primary select-none
          "
        >
          ☰
        </td>
      )}

      {children}
    </tr>
  )
}
